import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { AdminKeyService } from '../../services/adminKeyService';

type PlatformGroup = {
  id: string;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
};

const PlatformGroupsTab: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [items, setItems] = React.useState<PlatformGroup[]>([]);
  const [updatedAt, setUpdatedAt] = React.useState<string | null>(null);

  const [q, setQ] = React.useState('');

  const [editOpen, setEditOpen] = React.useState(false);
  const [editId, setEditId] = React.useState('');
  const [editName, setEditName] = React.useState('');
  const [editDescription, setEditDescription] = React.useState('');

  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState('');
  const [deleteName, setDeleteName] = React.useState('');

  const callApi = React.useCallback(async (action: string, payload?: any) => {
    const token = await AdminKeyService.getSecret();
    if (!token) throw new Error('Missing admin token');

    const res = await fetch('/api/platform-groups.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify({ action, ...(payload || {}) }),
    });

    const raw = await res.text();
    let json: any = null;
    try {
      json = raw ? JSON.parse(raw) : null;
    } catch {
      json = null;
    }

    if (!res.ok || !json?.ok) {
      const msg = json?.error || raw || `${res.status} ${res.statusText}`;
      throw new Error(msg);
    }

    return json;
  }, []);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      const json = await callApi('list');
      setItems(Array.isArray(json?.items) ? json.items : []);
      setUpdatedAt(typeof json?.updatedAt === 'string' ? json.updatedAt : null);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, [callApi]);

  React.useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditId('');
    setEditName('');
    setEditDescription('');
    setEditOpen(true);
  };

  const openEdit = (g: PlatformGroup) => {
    setEditId(g.id || '');
    setEditName(g.name || '');
    setEditDescription(g.description || '');
    setEditOpen(true);
  };

  const save = async () => {
    const name = editName.trim();
    if (!name) {
      toast.error('Name is required');
      return;
    }

    try {
      setSaving(true);
      await callApi('upsert', {
        group: {
          id: editId || undefined,
          name,
          description: editDescription.trim() || undefined,
        },
      });
      toast.success('Saved');
      setEditOpen(false);
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (g: PlatformGroup) => {
    setDeleteId(g.id);
    setDeleteName(g.name);
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setSaving(true);
      await callApi('delete', { id: deleteId });
      toast.success('Deleted');
      setDeleteOpen(false);
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  const filtered = React.useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return items;

    return items.filter((g) => {
      const hay = [g.id, g.name, g.description].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(query);
    });
  }, [items, q]);

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" fontWeight={900} gutterBottom>
              Platform Groups
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Saved locally in <code>storage/platform-groups.json</code>. Use groups to segment and target platform subscribers.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search name, description..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              onClick={load}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            >
              Refresh
            </Button>
            <Button variant="contained" onClick={openCreate} startIcon={<AddIcon />}>
              Add
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            {updatedAt ? `Last updated: ${updatedAt}` : 'Not saved yet'}
          </Typography>
          <Chip label={`${items.length} total`} variant="outlined" sx={{ fontWeight: 800 }} />
        </Box>

        <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((g) => (
                <TableRow key={g.id} hover>
                  <TableCell sx={{ fontWeight: 900 }}>{g.name}</TableCell>
                  <TableCell>{g.description || '-'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={g.id} variant="outlined" sx={{ fontFamily: 'monospace' }} />
                  </TableCell>
                  <TableCell>{g.updatedAt || '-'}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(g)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => confirmDelete(g)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}

              {filtered.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      No groups yet. Click “Add” to create your first platform group.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edit group' : 'Add group'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
              sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}
            />
            <TextField
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : undefined}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete group?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will remove <b>{deleteName}</b> from <code>platform-groups.json</code>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={doDelete} color="error" variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : undefined}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlatformGroupsTab;
